import { useRef, useState } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from '@/components/ui/sonner';

export interface UseAppFeatureToggleParams<TDto> {
  queryKey: QueryKey;
  /** Config in the cache: `undefined` while loading, `null` when not configured. */
  current: TDto | null | undefined;
  buildNext: (dto: TDto) => TDto;
  save: (next: TDto) => Promise<unknown>;
  errorMessage: string;
  onNotConfigured: () => void;
}

/**
 * Quick on/off of a hub feature (AD-031): PUT with the full DTO from the
 * cache, optimistic with rollback and toast, ignoring taps while it runs and
 * never creating a configuration (no config opens the editor).
 */
export function useAppFeatureToggle<TDto>({
  queryKey,
  current,
  buildNext,
  save,
  errorMessage,
  onNotConfigured,
}: UseAppFeatureToggleParams<TDto>): {
  toggle: () => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);
  const inFlight = useRef(false);

  function toggle() {
    if (inFlight.current) return;
    if (current === undefined) return;
    if (current === null) {
      onNotConfigured();
      return;
    }

    const next = buildNext(current);
    inFlight.current = true;
    setIsPending(true);
    queryClient.setQueryData(queryKey, next);
    save(next)
      .catch(() => {
        queryClient.setQueryData(queryKey, current);
        toast.error(errorMessage);
      })
      .finally(() => {
        inFlight.current = false;
        setIsPending(false);
      });
  }

  return { toggle, isPending };
}
